// AIP 문서 모니터링 대시보드 타입
// BE: /api/dashboard/aip/* (WorksAI/Model/AipDashboardModels.cs 와 필드명 일치)

export type UploadType = 'OneDrive' | 'SharePoint' | 'ChatAttachment' | 'KnowledgeEmbedding' | 'MailAttachment';

// uploadType → 화면 표시 라벨
export const uploadTypeLabel: Record<UploadType, string> = {
  OneDrive: 'OneDrive',
  SharePoint: 'SharePoint',
  ChatAttachment: '채팅 파일첨부',
  KnowledgeEmbedding: 'AI Makers',
  MailAttachment: 'Outlook',
};

// KPI + 출처별 집계
export interface AipSummary {
  totalCount: number; // 업로드된 문서 건수
  decryptSuccessCount: number; // AIP 복호화 성공
  decryptFailCount: number; // 복호화 실패
  privacyCount: number; // 개인정보 문서
  bySource: Array<{ uploadType: UploadType; count: number }>;
  loadedAt?: string; 
}

// 추이 집계 단위. 하루만 조회하면 시간대별(Hour), 여러 날이면 날짜별(Day)로 온다.
export type TrendUnit = 'Hour' | 'Day';

// 추이 항목
export interface AipTrendPoint {
  date: string; // Day면 2026-06-01, Hour면 14:00
  total: number;
  success: number;
  fail: number;
}

// 추이 응답
export interface AipTrend {
  unit: TrendUnit;
  points: AipTrendPoint[];
}

// 목록 아이템
export interface AipLogItem {
  id: string; // RowKey
  userId: string; // PartitionKey — 상세조회 키
  uploadedAt: string; // ISO
  userName: string;
  deptName?: string | null; // 소속(부서) — 로그에 없어 현재는 null
  labelName: string; // ProtectionDescriptorName
  fileName: string;
  isSuccess: boolean;
}

// 목록 응답 (BE QueryResult<T> 형태)
export interface AipLogListResult {
  data: AipLogItem[];
  totalCount: number;
  continuationToken?: string | null;
}

// 상세(단건)
export interface AipLogDetail {
  uploadedAt: string;
  labelName: string;
  fileName: string;
  uploadType: UploadType;
  sessionId: string;
  agentId: string | null; 
  agentName?: string | null; 
  isSuccess: boolean;
  failReason?: string | null;
}

// ────────────────────────── 기간 필터 ──────────────────────────

export type PeriodKey = 'today' | 'week' | 'month' | 'custom';

export const periodOptions = [
  { value: 'today', label: '오늘' },
  { value: 'week', label: '이번주' },
  { value: 'month', label: '이번달' },
  { value: 'custom', label: '기간' },
];

const toDateString = (date: Date) => {
  const y = date.getFullYear();
  const m = `${date.getMonth() + 1}`.padStart(2, '0');
  const d = `${date.getDate()}`.padStart(2, '0');
  return `${y}-${m}-${d}`;
};

/** 조회 가능한 최대 기간(개월). */
export const MAX_PERIOD_MONTHS = 1;

/**
 * 시작일 기준 조회 가능한 마지막 날짜.
 */
const getPeriodLimitDate = (from: Date): Date => {
  const limit = new Date(from);
  const targetMonth = from.getMonth() + MAX_PERIOD_MONTHS;

  limit.setMonth(targetMonth);

  // 날짜가 넘어갔으면 목표 월의 마지막 날로 보정한다.
  if (limit.getMonth() !== ((targetMonth % 12) + 12) % 12) {
    limit.setDate(0);
  }

  return limit;
};

/**
 * 기간이 최대 허용 범위를 넘는지 검사한다.
 */
const isPeriodExceeded = (from: string, to: string): boolean => {
  const fromDate = new Date(from);
  const toDate = new Date(to);

  if (isNaN(fromDate.getTime()) || isNaN(toDate.getTime())) return false;

  return toDate > getPeriodLimitDate(fromDate);
};

/**
 * 커스텀 기간 DatePicker에 넘길 최대 선택일(미래 차단).
 */
export const getMaxSelectableDate = (): string => toDateString(new Date());

/**
 * 선택한 [시작, 종료] 간격이 최대 조회 기간(1개월)을 넘는지 검사.
 */
export const isCustomRangeExceeded = (value: [string, string] | null): boolean => {
  if (!value?.[0] || !value[1]) return false;
  return isPeriodExceeded(value[0], value[1]);
};

/**
 * 기간 선택 → API 파라미터(from, to) 변환.
 */
export const resolvePeriodRange = (
  period: PeriodKey,
  customRange: [string, string] | null,
): { from: string; to: string } => {
  const today = new Date();

  switch (period) {
    case 'week': {
      const day = today.getDay(); 
      const diff = day === 0 ? 6 : day - 1;
      const monday = new Date(today);
      monday.setDate(today.getDate() - diff);
      return { from: toDateString(monday), to: toDateString(today) };
    }
    case 'month': {
      const first = new Date(today.getFullYear(), today.getMonth(), 1);
      return { from: toDateString(first), to: toDateString(today) };
    }
    case 'custom': {
      if (customRange?.[0] && customRange[1]) {
        const [from, to] = customRange;

        if (isPeriodExceeded(from, to)) {
          return { from, to: toDateString(getPeriodLimitDate(new Date(from))) };
        }

        return { from, to };
      }
      return { from: toDateString(today), to: toDateString(today) };
    }
    case 'today':
    default:
      return { from: toDateString(today), to: toDateString(today) };
  }
};
